/**
 * Перенос `lib/src/utils/server_address.dart`.
 *
 * Класс оставлен классом: у Dart-версии есть геттеры `root`/`url`/`hasPath`,
 * и вызовы вида `address.root` переносятся один в один. Поля неизменяемы,
 * нового адреса без конструктора не получить.
 */

/** Схема, вставленная в текст явно: `^\s*(https?)://`, регистр не важен. */
const SCHEME_PREFIX = /^\s*(https?):\/\//i;

/** Любой пробельный символ — при разборе адрес чистится от них целиком. */
const WHITESPACE = /\s+/g;

/** Слеши по краям пути. */
const EDGE_SLASHES = /^\/+|\/+$/g;

/** Хост, начинающийся с цифры, — это IP-адрес. */
const LEADING_DIGIT = /^\d/;

export interface ServerAddressInit {
  readonly scheme: string;
  readonly host: string;
  readonly path?: string;
}

/**
 * Адрес сервера в том виде, в каком его вводит администратор.
 *
 * Схему набирать не нужно: у IP-адресов и `localhost` (локальные серверы)
 * это `http`, у доменов — `https`. Явно вставленная схема важнее догадки.
 */
export class ServerAddress {
  static readonly http = 'http';
  static readonly https = 'https';

  /** `http` или `https`. */
  readonly scheme: string;

  /** Хост с портом: `lp.hron.uz`, `192.168.68.67:8001`. */
  readonly host: string;

  /**
   * Путь без слешей по краям — обычно slug планшета. Пусто, если введён
   * только домен.
   */
  readonly path: string;

  constructor({ scheme, host, path = '' }: ServerAddressInit) {
    this.scheme = scheme;
    this.host = host;
    this.path = path;
  }

  get isEmpty(): boolean {
    return this.host.length === 0;
  }

  get hasPath(): boolean {
    return this.path.length > 0;
  }

  /** `https://lp.hron.uz` */
  get root(): string {
    return `${this.scheme}://${this.host}`;
  }

  /** `https://lp.hron.uz/cic_work_test` — в таком виде адрес хранится. */
  get url(): string {
    return this.hasPath ? `${this.root}/${this.path}` : this.root;
  }

  /** Схема, вставленная в текст явно, или `null`. */
  static explicitScheme(input: string): string | null {
    const match = SCHEME_PREFIX.exec(input);
    return match === null ? null : match[1].toLowerCase();
  }

  /** Текст без схемы. Остальное не трогается — человек ещё печатает. */
  static stripScheme(input: string): string {
    return input.replace(SCHEME_PREFIX, '');
  }

  /** `http` для IP и `localhost`, `https` для доменов. */
  static guessScheme(input: string): string {
    const text = ServerAddress.stripScheme(input).trim().toLowerCase();
    const isLocal = LEADING_DIGIT.test(text) || text.startsWith('localhost');
    return isLocal ? ServerAddress.http : ServerAddress.https;
  }

  /**
   * Разбирает ввод. [scheme] — схема, выбранная вручную. Явная схема в тексте
   * важнее неё, догадка по хосту — последний вариант.
   */
  static parse(input: string, scheme?: string | null): ServerAddress {
    const rest = ServerAddress.stripScheme(input).replace(WHITESPACE, '');
    const slash = rest.indexOf('/');
    const host = slash < 0 ? rest : rest.substring(0, slash);
    const path = slash < 0 ? '' : rest.substring(slash + 1).replace(EDGE_SLASHES, '');
    return new ServerAddress({
      scheme:
        ServerAddress.explicitScheme(input) ?? scheme ?? ServerAddress.guessScheme(host),
      host,
      path,
    });
  }
}
